import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import {
  StaffCheckInService,
  StudentCheckInRecord,
  CheckInFilterOption,
} from '../services/staff-checkin.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;

const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 500;
const ALL = 'All';
const DATE_FORMAT = 'YYYY-MM-DD';

@Component({
  selector: 'app-staff-checkin',
  templateUrl: './staff-checkin.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-checkin.component.css']
})
export class StaffCheckInComponent implements OnInit {
  staffId: number | string | null = null;
  academicStartDateObj: Date | null = null;
  academicEndDateObj: Date | null = null;

  // ---------- List state ----------
  students: StudentCheckInRecord[] = [];
  totalStudents = 0;
  totalCheckedIn = 0;
  totalCheckedOut = 0;

  pageNumber = 1;
  maxPageNumber = 1;
  fetchingData = true;
  searching = false;
  applyingFilter = false;
  loadingMore = false;

  readonly skeletonRows = Array.from({ length: 10 }, () => ({}));

  get isLoading(): boolean {
    return this.fetchingData || this.searching || this.applyingFilter;
  }

  // ---------- Search ----------
  searchOpen = false;
  searchString = '';
  private searchDebounceTimer: any;
  private isFirstLoad = true;

  // ---------- Applied filters (drive the API call) ----------
  filterDate: string = moment().format(DATE_FORMAT);
  selectedClassWing = ALL;
  selectedClassStandard = ALL;
  selectedClassId = ALL;

  // ---------- Filter sheet (draft state, only applied on "Apply Filter") ----------
  showFilterSheet = false;
  draftDate: string = this.filterDate;
  draftDateObj: Date = moment(this.filterDate, DATE_FORMAT).toDate();
  draftClassWing = ALL;
  draftClassStandard = ALL;
  draftClassId = ALL;

  wingOptions: CheckInFilterOption[] = [];
  classStandardOptions: CheckInFilterOption[] = [];
  sectionOptions: CheckInFilterOption[] = [];
  loadingWings = false;
  loadingClassStandards = false;
  loadingSections = false;

  constructor(
    private staffCheckInService: StaffCheckInService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
    this.academicStartDateObj = info?.academicStartDate ? moment(info.academicStartDate).toDate() : null;
    this.academicEndDateObj = info?.academicEndDate ? moment(info.academicEndDate).toDate() : null;
    this.loadStudents(1);
  }

  // ---------- List loading ----------

  async loadStudents(page: number) {
    if (!this.staffId) { this.fetchingData = false; return; }
    try {
      const response = await firstValueFrom(this.staffCheckInService.getStudentCheckInList({
        staffId: this.staffId,
        classCategoryId: this.selectedClassWing,
        classId: this.selectedClassId,
        classStandardId: this.selectedClassStandard,
        searchedStudentName: this.searchString,
        pageNumber: page,
        pageLimit: PAGE_LIMIT,
        filterDate: this.filterDate,
      }));
      if (response?.status && response.body) {
        const newRecords = response.body.studentCheckInCheckOutData || [];
        // studentData is cleared to [] right before search/filter-triggered
        // fetches, so an "append" here is effectively a fresh replace in
        // those cases too — matches the mobile app's own approach.
        this.students = page === 1 ? newRecords : [...this.students, ...newRecords];
        this.totalStudents = response.body.totalStudents || 0;
        this.totalCheckedIn = response.body.totalCheckedInStudents || 0;
        this.totalCheckedOut = response.body.totalCheckedOutStudents || 0;
        this.maxPageNumber = response.body.maxPageNumber || 1;
        this.pageNumber = page;
      } else {
        this.students = [];
        const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
        customToastr(response?.message || message, 'error');
      }
    } catch {
      this.students = [];
      const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(message, 'error');
    } finally {
      this.fetchingData = false;
      this.searching = false;
      this.applyingFilter = false;
      this.loadingMore = false;
      this.isFirstLoad = false;
    }
  }

  loadMore() {
    if (this.pageNumber >= this.maxPageNumber) { return; }
    if (this.isLoading || this.loadingMore) { return; }
    this.loadingMore = true;
    this.loadStudents(this.pageNumber + 1);
  }

  refreshList() {
    // Pull-to-refresh: re-fetch page 1 with current filters/search intact.
    this.students = [];
    this.fetchingData = true;
    this.loadStudents(1);
  }

  // ---------- Search ----------

  toggleSearch() {
    this.searchOpen = !this.searchOpen;
    if (!this.searchOpen) {
      this.searchString = '';
    }
  }

  onSearchInput(value: string) {
    this.searchString = value;
    if (this.isFirstLoad) { return; }
    this.searching = true;
    this.students = [];
    this.pageNumber = 1;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.loadStudents(1), SEARCH_DEBOUNCE_MS);
  }

  // ---------- Filter sheet ----------

  async openFilterSheet() {
    this.draftDate = this.filterDate;
    this.draftDateObj = moment(this.filterDate, DATE_FORMAT).toDate();
    this.draftClassWing = this.selectedClassWing;
    this.draftClassStandard = this.selectedClassStandard;
    this.draftClassId = this.selectedClassId;
    this.classStandardOptions = [];
    this.sectionOptions = [];
    this.showFilterSheet = true;
    await this.loadWingOptions();
  }

  closeFilterSheet() {
    this.showFilterSheet = false;
  }

  private async loadWingOptions() {
    this.loadingWings = true;
    try {
      const response = await firstValueFrom(this.staffCheckInService.getWingOptions());
      this.wingOptions = response?.status ? (response.body?.classCategoryListDetails || []) : [];
    } catch {
      this.wingOptions = [];
    } finally {
      this.loadingWings = false;
    }
  }

  // Wing → Class Standard → Section is a strict cascade: picking a wing
  // clears class-standard/section selections and their option lists.
  async onDraftWingChange(wing: string) {
    this.draftClassWing = wing;
    this.draftClassStandard = ALL;
    this.draftClassId = ALL;
    this.classStandardOptions = [];
    this.sectionOptions = [];
    if (wing === ALL) { return; }
    this.loadingClassStandards = true;
    try {
      const response = await firstValueFrom(this.staffCheckInService.getClassOptions(wing));
      this.classStandardOptions = response?.status ? (response.body?.classCategoryListData || []) : [];
    } catch {
      this.classStandardOptions = [];
    } finally {
      this.loadingClassStandards = false;
    }
  }

  async onDraftClassStandardChange(classStandard: string) {
    this.draftClassStandard = classStandard;
    this.draftClassId = ALL;
    this.sectionOptions = [];
    if (classStandard === ALL) { return; }
    this.loadingSections = true;
    try {
      const response = await firstValueFrom(this.staffCheckInService.getSectionOptions(this.draftClassWing, classStandard));
      this.sectionOptions = response?.status ? (response.body?.classSectionListData || []) : [];
    } catch {
      this.sectionOptions = [];
    } finally {
      this.loadingSections = false;
    }
  }

  onDraftClassIdChange(classId: string) {
    this.draftClassId = classId;
  }

  onDraftDateChange(date: Date) {
    this.draftDateObj = date;
    this.draftDate = moment(date).format(DATE_FORMAT);
    // Selecting a date directly applies immediately and closes the sheet —
    // dropdown changes only apply when "Apply Filter" is tapped.
    this.filterDate = this.draftDate;
    this.showFilterSheet = false;
    this.applyFiltersAndRefetch();
  }

  get hasActiveFilters(): boolean {
    return this.selectedClassWing !== ALL
      || this.selectedClassStandard !== ALL
      || this.selectedClassId !== ALL
      || this.filterDate !== moment().format(DATE_FORMAT);
  }

  clearFilter() {
    if (!this.hasActiveFilters) { return; }
    this.selectedClassWing = ALL;
    this.selectedClassStandard = ALL;
    this.selectedClassId = ALL;
    this.filterDate = moment().format(DATE_FORMAT);
    this.draftDateObj = moment(this.filterDate, DATE_FORMAT).toDate();
    this.showFilterSheet = false;
    this.applyFiltersAndRefetch();
  }

  applyFilter() {
    this.selectedClassWing = this.draftClassWing;
    this.selectedClassStandard = this.draftClassStandard;
    this.selectedClassId = this.draftClassId;
    this.filterDate = this.draftDate;
    this.showFilterSheet = false;
    this.applyFiltersAndRefetch();
  }

  private applyFiltersAndRefetch() {
    this.applyingFilter = true;
    this.students = [];
    this.pageNumber = 1;
    this.loadStudents(1);
  }

  callNumber(number: string) {
    if (number) {
      window.open(`tel:${number}`, '_self');
    }
  }
}
